import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { 
  Mail, Globe, Plus, Trash2, Copy, Check, 
  BookOpen, Upload, Brain, FileText, HelpCircle, DollarSign,
  Loader2, CheckCircle, AlertCircle, ExternalLink, ChevronRight,
  Building2, ShoppingBag, Package, Link2, Unlink
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Platform configurations with logos and colors
const PLATFORMS = [
  {
    id: 'indiamart',
    name: 'IndiaMART',
    logo: '🏪',
    color: 'bg-blue-500',
    description: 'India\'s largest B2B marketplace',
    type: 'email_forward'
  },
  {
    id: 'alibaba',
    name: 'Alibaba.com',
    logo: '🌐',
    color: 'bg-orange-500',
    description: 'Global wholesale trade platform',
    type: 'email_forward'
  },
  {
    id: 'tradeindia',
    name: 'TradeIndia',
    logo: '🇮🇳',
    color: 'bg-green-500',
    description: 'Indian B2B marketplace',
    type: 'email_forward'
  },
  {
    id: 'exportersindia',
    name: 'ExportersIndia',
    logo: '📦',
    color: 'bg-purple-500',
    description: 'Export-focused marketplace',
    type: 'email_forward'
  },
  {
    id: 'website',
    name: 'Your Website',
    logo: '🌍',
    color: 'bg-slate-700',
    description: 'Capture leads from your own website',
    type: 'website'
  }
];

export default function Settings() {
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('lead-sources');
  
  // Lead Sources state
  const [leadSources, setLeadSources] = useState([]);
  const [isLoadingSources, setIsLoadingSources] = useState(true);
  const [showAddSource, setShowAddSource] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [setupInstructions, setSetupInstructions] = useState(null);
  const [isCreatingSource, setIsCreatingSource] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(null);
  
  // Knowledge Base state
  const [knowledgeBase, setKnowledgeBase] = useState(null);
  const [isLoadingKB, setIsLoadingKB] = useState(true);
  const [uploadType, setUploadType] = useState('faq');
  const [uploadContent, setUploadContent] = useState('');
  const [uploadTitle, setUploadTitle] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isTraining, setIsTraining] = useState(false);

  // Connected Accounts state
  const [isGoogleConnected, setIsGoogleConnected] = useState(false);
  const [googleProfile, setGoogleProfile] = useState(null);
  const [isLinkingGoogle, setIsLinkingGoogle] = useState(false);

  const fetchLeadSources = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/lead-sources`);
      setLeadSources(response.data);
    } catch (error) {
      toast.error('Failed to fetch lead sources');
    } finally {
      setIsLoadingSources(false);
    }
  }, [API]);

  const fetchKnowledgeBase = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/knowledge-base`);
      setKnowledgeBase(response.data);
    } catch (error) {
      toast.error('Failed to fetch knowledge base');
    } finally {
      setIsLoadingKB(false);
    }
  }, [API]);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    setUser(userData);
    
    // Check if Google is connected
    if (userData) {
      setIsGoogleConnected(userData.google_connected || false);
      if (userData.google_connected) {
        setGoogleProfile({
          name: userData.google_name,
          picture: userData.google_picture || userData.picture
        });
      }
    }
    
    if (userData?.role === 'seller') {
      fetchLeadSources();
      fetchKnowledgeBase();
    }
  }, [fetchLeadSources, fetchKnowledgeBase]);

  const handleConnectPlatform = async (platform) => {
    setSelectedPlatform(platform);
    setIsCreatingSource(true);
    
    try {
      const response = await axios.post(`${API}/lead-sources`, {
        source_type: platform.id,
        name: platform.name
      });
      
      setLeadSources([...leadSources, response.data.source]);
      setSetupInstructions(response.data.setup_instructions);
      setShowAddSource(false);
      setShowSetupDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create lead source');
    } finally {
      setIsCreatingSource(false);
    }
  };

  const handleDeleteSource = async (sourceId) => {
    try {
      await axios.delete(`${API}/lead-sources/${sourceId}`);
      setLeadSources(leadSources.filter(s => s.id !== sourceId));
      toast.success('Lead source disconnected');
    } catch (error) {
      toast.error('Failed to delete lead source');
    }
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedEmail(id);
    toast.success('Email copied to clipboard!');
    setTimeout(() => setCopiedEmail(null), 2000);
  };

  const handleUploadContent = async () => {
    if (!uploadContent.trim()) {
      toast.error('Please enter content to upload');
      return;
    }
    
    setIsUploading(true);
    try {
      const response = await axios.post(`${API}/knowledge-base/upload`, {
        content_type: uploadType,
        content: uploadContent,
        title: uploadTitle || undefined
      });
      
      toast.success(`Content uploaded successfully!`);
      setUploadContent('');
      setUploadTitle('');
      fetchKnowledgeBase();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload content');
    } finally {
      setIsUploading(false);
    }
  };

  const handleTrainKB = async () => {
    setIsTraining(true);
    try {
      const response = await axios.post(`${API}/knowledge-base/train`);
      toast.success(response.data.summary);
      fetchKnowledgeBase();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to train knowledge base');
    } finally {
      setIsTraining(false);
    }
  };

  const getConnectedPlatforms = () => {
    return leadSources.map(s => s.source_type);
  };

  if (user?.role !== 'seller') {
    return (
      <Layout>
        <div className="max-w-4xl mx-auto px-4 py-8 text-center">
          <h1 className="text-2xl font-bold text-primary mb-4">Settings</h1>
          <p className="text-muted-foreground">Settings are available for sellers only.</p>
        </div>
      </Layout>
    );
  }

  const connectedPlatforms = getConnectedPlatforms();

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="settings-page">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-primary mb-2">Settings</h1>
          <p className="text-muted-foreground">
            Connect your marketplaces, train your AI assistant, and configure automation
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="lead-sources" data-testid="tab-lead-sources">
              <Mail className="h-4 w-4 mr-2" />
              Lead Sources
            </TabsTrigger>
            <TabsTrigger value="knowledge-base" data-testid="tab-knowledge-base">
              <BookOpen className="h-4 w-4 mr-2" />
              Knowledge Base
            </TabsTrigger>
            <TabsTrigger value="connected-accounts" data-testid="tab-connected-accounts">
              <Link2 className="h-4 w-4 mr-2" />
              Connected Accounts
            </TabsTrigger>
          </TabsList>

          {/* Lead Sources Tab */}
          <TabsContent value="lead-sources">
            {/* Connected Sources */}
            {leadSources.length > 0 && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Connected Platforms</CardTitle>
                  <CardDescription>Your active lead capture integrations</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {leadSources.map((source) => {
                      const platform = PLATFORMS.find(p => p.id === source.source_type) || PLATFORMS[0];
                      return (
                        <div 
                          key={source.id}
                          className="flex items-center justify-between p-4 border rounded-lg bg-slate-50"
                          data-testid={`lead-source-${source.id}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-lg ${platform.color} flex items-center justify-center text-white text-xl`}>
                              {platform.logo}
                            </div>
                            <div>
                              <h4 className="font-medium">{source.name}</h4>
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Connected
                                </Badge>
                                <span>•</span>
                                <span>{source.leads_captured} leads captured</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {source.forward_email && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => copyToClipboard(source.forward_email, source.id)}
                                className="font-mono text-xs"
                              >
                                {copiedEmail === source.id ? (
                                  <><Check className="h-3 w-3 mr-1 text-green-600" /> Copied!</>
                                ) : (
                                  <><Copy className="h-3 w-3 mr-1" /> {source.forward_email}</>
                                )}
                              </Button>
                            )}
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleDeleteSource(source.id)}
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Connect Marketplaces - Coming Soon */}
            <Card className="border-dashed border-2 border-amber-300 bg-amber-50/50" data-testid="connect-marketplaces-coming-soon">
              <CardContent className="py-8">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-100 mb-4">
                    <ShoppingBag className="h-8 w-8 text-amber-600" />
                  </div>
                  <Badge className="mb-3 bg-amber-500 hover:bg-amber-500">Coming Soon</Badge>
                  <h3 className="text-lg font-semibold text-amber-900 mb-2">Marketplace Integrations</h3>
                  <p className="text-amber-700 max-w-md mx-auto mb-4">
                    Connect your IndiaMART, Alibaba, TradeIndia, and other B2B marketplace accounts to automatically capture leads in one place.
                  </p>
                  <div className="flex items-center justify-center gap-4 flex-wrap">
                    {PLATFORMS.slice(0, 4).map((platform) => (
                      <div key={platform.id} className="flex items-center gap-2 opacity-60">
                        <span className="text-xl">{platform.logo}</span>
                        <span className="text-sm font-medium text-amber-800">{platform.name}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-amber-600 mt-4">
                    We're working hard to bring this feature to you soon. Stay tuned!
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Knowledge Base Tab */}
          <TabsContent value="knowledge-base">
            <div className="grid gap-6 md:grid-cols-2">
              {/* Training Status */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Brain className="h-5 w-5" />
                    AI Training Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {isLoadingKB ? (
                    <Loader2 className="h-6 w-6 animate-spin" />
                  ) : (
                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        {knowledgeBase?.is_trained ? (
                          <CheckCircle className="h-8 w-8 text-green-500" />
                        ) : (
                          <AlertCircle className="h-8 w-8 text-amber-500" />
                        )}
                        <div>
                          <p className="font-medium">
                            {knowledgeBase?.is_trained ? 'AI Trained' : 'Training Needed'}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {knowledgeBase?.training_summary || 'Add content and train your AI'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                        <div className="text-center">
                          <p className="text-2xl font-bold">{knowledgeBase?.products?.length || 0}</p>
                          <p className="text-xs text-muted-foreground">Products</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold">{knowledgeBase?.faqs?.length || 0}</p>
                          <p className="text-xs text-muted-foreground">FAQs</p>
                        </div>
                      </div>

                      <Button 
                        onClick={handleTrainKB}
                        disabled={isTraining}
                        className="w-full"
                        data-testid="train-kb-btn"
                      >
                        {isTraining ? (
                          <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Training...</>
                        ) : (
                          <><Brain className="h-4 w-4 mr-2" /> Train AI Now</>
                        )}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Upload Content */}
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Upload className="h-5 w-5" />
                    Upload Content
                  </CardTitle>
                  <CardDescription>
                    Add FAQs, product catalogs, or pricing info to train your AI
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex gap-2">
                      {[
                        { id: 'faq', label: 'FAQs', icon: HelpCircle },
                        { id: 'catalog', label: 'Catalog', icon: Package },
                        { id: 'pricing', label: 'Pricing', icon: DollarSign }
                      ].map((type) => (
                        <Button
                          key={type.id}
                          variant={uploadType === type.id ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setUploadType(type.id)}
                        >
                          <type.icon className="h-3 w-3 mr-1" />
                          {type.label}
                        </Button>
                      ))}
                    </div>
                    
                    <Textarea
                      placeholder={uploadType === 'faq' 
                        ? "Q: What is your MOQ?\nA: Minimum order is 1000 units.\n\nQ: Do you offer samples?\nA: Yes, samples available at cost."
                        : "Paste your content here..."
                      }
                      value={uploadContent}
                      onChange={(e) => setUploadContent(e.target.value)}
                      rows={6}
                      data-testid="upload-content-textarea"
                    />
                    
                    <Button 
                      onClick={handleUploadContent}
                      disabled={isUploading}
                      className="w-full"
                      data-testid="upload-content-btn"
                    >
                      {isUploading ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</>
                      ) : (
                        <><Upload className="h-4 w-4 mr-2" /> Upload Content</>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Current FAQs */}
            {knowledgeBase?.faqs?.length > 0 && (
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle className="font-heading">Current FAQs</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {knowledgeBase.faqs.slice(0, 5).map((faq, idx) => (
                      <div key={idx} className="p-3 bg-slate-50 rounded-lg">
                        <p className="font-medium text-sm">Q: {faq.question}</p>
                        <p className="text-sm text-muted-foreground mt-1">A: {faq.answer}</p>
                      </div>
                    ))}
                    {knowledgeBase.faqs.length > 5 && (
                      <p className="text-sm text-muted-foreground text-center">
                        +{knowledgeBase.faqs.length - 5} more FAQs
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Connected Accounts Tab */}
          <TabsContent value="connected-accounts">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading text-lg">Connected Accounts</CardTitle>
                <CardDescription>
                  Link external accounts for easier sign-in and enhanced features
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Google Account */}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm border">
                      <svg className="w-6 h-6" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium">Google</p>
                      {isGoogleConnected ? (
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Connected
                          </Badge>
                          {googleProfile?.name && (
                            <span className="text-sm text-muted-foreground">
                              as {googleProfile.name}
                            </span>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          Sign in faster with your Google account
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {isGoogleConnected ? (
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('token');
                          await axios.post(`${API}/auth/unlink-google`, {}, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          setIsGoogleConnected(false);
                          setGoogleProfile(null);
                          // Update local storage
                          const userData = JSON.parse(localStorage.getItem('user'));
                          userData.google_connected = false;
                          localStorage.setItem('user', JSON.stringify(userData));
                          toast.success('Google account unlinked');
                        } catch (error) {
                          toast.error('Failed to unlink Google account');
                        }
                      }}
                      data-testid="unlink-google-btn"
                    >
                      <Unlink className="h-4 w-4 mr-2" />
                      Unlink
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={isLinkingGoogle}
                      onClick={() => {
                        setIsLinkingGoogle(true);
                        const currentUrl = window.location.origin;
                        // Store current user info to link after OAuth
                        sessionStorage.setItem('linkGoogleToUser', user?.id || '');
                        const callbackUrl = `${currentUrl}/auth/callback?link=true`;
                        window.location.href = `https://demobackend.emergentagent.com/auth/v1/env/oauth/redirect?callback_url=${encodeURIComponent(callbackUrl)}`;
                      }}
                      data-testid="link-google-btn"
                    >
                      {isLinkingGoogle ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Link2 className="h-4 w-4 mr-2" />
                      )}
                      Link Account
                    </Button>
                  )}
                </div>

                {/* Benefits info */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <h4 className="font-medium text-sm mb-2">Benefits of linking accounts:</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-green-500" />
                      Sign in with one click using Google
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-green-500" />
                      No need to remember another password
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-green-500" />
                      Enhanced account security with Google's 2FA
                    </li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Setup Instructions Dialog */}
        <Dialog open={showSetupDialog} onOpenChange={setShowSetupDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                {setupInstructions?.title || 'Setup Instructions'}
              </DialogTitle>
              <DialogDescription>
                Follow these steps to complete your integration
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4 space-y-4">
              {/* Email to Copy */}
              {setupInstructions?.forward_email && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm font-medium text-amber-800 mb-2">Your Unique Forwarding Email:</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 p-2 bg-white rounded border text-sm font-mono">
                      {setupInstructions.forward_email}
                    </code>
                    <Button 
                      size="sm"
                      onClick={() => copyToClipboard(setupInstructions.forward_email, 'setup')}
                    >
                      {copiedEmail === 'setup' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              )}

              {/* Steps */}
              <div className="space-y-3">
                {setupInstructions?.steps?.map((step, idx) => (
                  <div key={idx} className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary text-white text-sm flex items-center justify-center flex-shrink-0">
                      {step.step || idx + 1}
                    </div>
                    <div>
                      <p className="font-medium text-sm">{step.title}</p>
                      <p className="text-sm text-muted-foreground">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Note */}
              {setupInstructions?.note && (
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">{setupInstructions.note}</p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button onClick={() => setShowSetupDialog(false)}>
                Done
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
